"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Check,
  AlertCircle,
  ArrowLeft,
  FileText,
  Clock,
  Camera,
  Edit,
  Loader,
  Award,
  CheckCircle2,
  Pen,
  ImageIcon,
  ChevronDown,
  ChevronUp,
  X,
  CheckCircle,
  XCircle,
} from "lucide-react"
import { getAssignmentData, getSubmissionDetials } from "./api"
import { generatePresignedUrl, postStudentAnsFile } from "../postFileAws"
import { postAws } from "../aws"
import ReactMarkdown from "react-markdown"

function decodeJWT(jwtToken) {
  const [header, payload, signature] = jwtToken.split(".")
  const decodedHeader = JSON.parse(atob(header))
  const decodedPayload = JSON.parse(atob(payload))

  return {
    header: decodedHeader,
    payload: decodedPayload,
    signature: signature,
  }
}

const ImageDropdown = ({ imageUrl, onRemove, canRemove }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-50"
        >
          <span>Answer Image</span>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {canRemove && (
          <button
            onClick={onRemove}
            className="ml-2 p-1 text-red-600 hover:text-red-800 focus:outline-none"
            aria-label="Remove image"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      {isOpen && (
        <div className="mt-2">
          <img
            src={imageUrl || "/placeholder.svg?height=300&width=500"}
            alt="Answer"
            className="w-full rounded-lg shadow-lg"
            onError={(e) => {
              e.target.onerror = null
              e.target.src = "/placeholder.svg?height=300&width=500"
            }}
          />
        </div>
      )}
    </div>
  )
}

const renderMarkdown = (text) => {
  return (
    <ReactMarkdown
      components={{
        h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-3" {...props} />,
        h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-2" {...props} />,
        h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2" {...props} />,
        h4: ({ node, ...props }) => <h4 className="text-base font-semibold mt-3 mb-1" {...props} />,
        p: ({ node, ...props }) => <p className="mb-3" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3" {...props} />,
        li: ({ node, ...props }) => <li className="mb-1" {...props} />,
        strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

export default function SubmittedAssignmentDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [assignment, setAssignment] = useState(null)
  const [questionarr, setQuestionarr] = useState([])
  const [uploads, setUploads] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [answerMethod, setAnswerMethod] = useState({})
  const [showWarning, setShowWarning] = useState(false)
  const [studentId, setStudentId] = useState()
  const [loadingImageIndex, setLoadingImageIndex] = useState(null)
  const [questionImages, setQuestionImages] = useState({})
  const [activeQuestion, setActiveQuestion] = useState(0)
  const [totalMarks, setTotalMarks] = useState(0)
  const [dueDate, setDueDate] = useState("No due date")
  const [assignmentStatus, setAssignmentStatus] = useState("new") // new, saved, submitted, evaluated
  const [submissionId, setSubmissionId] = useState(null)
  const [dueDatePassed, setDueDatePassed] = useState(false)
  const [userAnswers, setUserAnswers] = useState({})
  const [submissionData, setSubmissionData] = useState(null)
  const organisationId = localStorage.getItem("organisationId")

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check if this is a saved assignment
        try {
          const submissionResponse = await getSubmissionDetials(id)
          if (submissionResponse?.body?.data) {
            const data = submissionResponse.body.data
            setSubmissionData(data)
            setAssignment(data)
            setQuestionarr(data.assignmentQuestion || [])
            setAssignmentStatus(data.status || "saved")
            setSubmissionId(data._id)

            // Initialize user answers
            const initialAnswers = {}
            data.assignmentQuestion.forEach((question, index) => {
              initialAnswers[index] = question.user_answer || ""
            })
            setUserAnswers(initialAnswers)

            // Calculate total marks
            const total = data.assignmentQuestion.reduce((sum, question) => {
              return sum + (question.maxMarks || 0)
            }, 0)
            setTotalMarks(total)

            // Format due date
            if (data.assignmentDueDate) {
              const date = new Date(data.assignmentDueDate)
              if (!isNaN(date.getTime())) {
                setDueDate(
                  date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  }),
                )

                // Check if due date has passed
                const dueDateTime = new Date(date)
                dueDateTime.setHours(23, 59, 59, 999)
                setDueDatePassed(new Date() > dueDateTime)
              }
            }

            // Fetch presigned URLs for question images
            const imageUrls = {}
            const updatedQuestions = await Promise.all(
              data.assignmentQuestion.map(async (question, index) => {
                const updatedQuestion = { ...question }

                // Handle question images
                if (question.questionImageUrl) {
                  try {
                    const url = await generatePresignedUrl(question.questionImageUrl)
                    imageUrls[question.questionImageUrl] = url
                    setQuestionImages((prev) => ({ ...prev, [index]: url }))
                  } catch (error) {
                    console.error("Error generating presigned URL:", error)
                  }
                }

                // Handle user uploaded images - get presigned URLs
                if (question.user_image) {
                  try {
                    const url = await generatePresignedUrl(question.user_image)
                    updatedQuestion.user_displayImage = url
                  } catch (error) {
                    console.error("Error generating presigned URL for user image:", error)
                  }
                }

                return updatedQuestion
              }),
            )
            setQuestionarr(updatedQuestions)

            return // Exit if we found a submission
          }
        } catch (error) {
          console.log("No saved submission found, fetching new assignment")
        }

        // If no submission found, fetch new assignment
        const response = await getAssignmentData(id)
        setAssignment(response)

        if (response.assignmentQuestion) {
          setQuestionarr(response.assignmentQuestion)

          // Calculate total marks
          const total = response.assignmentQuestion.reduce((sum, question) => {
            return sum + (question.maxMarks || 0)
          }, 0)
          setTotalMarks(total)
        }

        // Format due date
        if (response.assignmentDueDate) {
          const date = new Date(response.assignmentDueDate)
          if (!isNaN(date.getTime())) {
            setDueDate(
              date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
            )

            // Check if due date has passed
            const dueDateTime = new Date(date)
            dueDateTime.setHours(23, 59, 59, 999)
            setDueDatePassed(new Date() > dueDateTime)
          }
        }

        getTokenDetailsFromLocalStorage()

        // Fetch presigned URLs for question images
        const imageUrls = {}
        for (const question of response.assignmentQuestion) {
          if (question.questionImageUrl) {
            try {
              const url = await generatePresignedUrl(question.questionImageUrl)
              imageUrls[question.questionImageUrl] = url
            } catch (error) {
              console.error("Error generating presigned URL:", error)
            }
          }
        }
        setQuestionImages(imageUrls)
      } catch (error) {
        console.error("Error fetching assignment:", error)
      }
    }

    function getTokenDetailsFromLocalStorage() {
      const jwtToken = localStorage.getItem("token")
      if (jwtToken) {
        const tokenDetails = decodeJWT(jwtToken)
        setStudentId(tokenDetails.payload.user.id)
      } else {
        console.log("No token found in localStorage")
      }
    }

    fetchData()
  }, [id])

  // Interactive functions for saved assignments
  const handleFileUpload = (questionId, file) => {
    setUploads((prev) => ({
      ...prev,
      [questionId]: file,
    }))
    setAnswerMethod((prev) => ({
      ...prev,
      [questionId]: "pdf",
    }))
  }

  const handleTextAnswer = (index, value) => {
    const updatedQuestions = [...questionarr]
    updatedQuestions[index].user_answer = value
    setQuestionarr(updatedQuestions)
  }

  const handleMCQAnswer = (index, selectedOption) => {
    const updatedQuestions = [...questionarr]
    updatedQuestions[index].user_answer = selectedOption
    setQuestionarr(updatedQuestions)
  }

  const handlePhotoCapture = async (index, file) => {
    if (!file) {
      console.log("No file selected")
      return
    }

    setLoadingImageIndex(index)

    try {
      const studentId = localStorage.getItem("studentId")
      const response = await postStudentAnsFile(file, studentId, id, `question${index}`)
      const uploadedUrl = await generatePresignedUrl(response)
      const updatedQuestions = [...questionarr]
      updatedQuestions[index].user_image = response
      updatedQuestions[index].user_displayImage = uploadedUrl
      setQuestionarr(updatedQuestions)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingImageIndex(null)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    const studentId = localStorage.getItem("studentId")
    const classRoomId=localStorage.getItem("classRoomId")
    const submission = {
      body: {
        assignmentId: assignment.assignmentId,
        teacherId: assignment.teacherId,
        assignmentQuestion: questionarr,
        studentId: studentId,
        organisationId: organisationId,
        classRoomId: classRoomId,
        status: "saved",
      },
      request: "save-Assignment",
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/submission", "POST", submission)
      setTimeout(() => {
        setSaving(false)
        navigate("/assignments")
      }, 3000)
    } catch (err) {
      console.error(`Couldn't submit assignment: ${err}`)
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    const unansweredQuestions = questionarr.filter((_, index) => !isQuestionAnswered(index)).length
    if (unansweredQuestions > 0) {
      setShowWarning(true)
    } else {
      submitAssignment()
    }
  }

   const submitAssignment = async () => {
     setSubmitting(true)
     const studentId = localStorage.getItem("studentId")
      const classRoomId=localStorage.getItem("classRoomId")
     const submission = {
       body: {
         assignmentId: assignment.assignmentId,
         teacherId: assignment.teacherId,
         assignmentQuestion: questionarr,
         studentId: studentId,
         organisationId: organisationId,
         classRoomId: classRoomId,
         status: "submitted",
       },
       request: "submit-Assignment",
     }
 
     try {
       const response = await postAws("https://api-dev.aurait.ai/submission", "POST", submission)
       setTimeout(() => {
         setSubmitting(false)
         navigate("/assignments")
       }, 3000)
     } catch (err) {
       console.error(`Couldn't submit assignment: ${err}`)
       setSubmitting(false)
     }
   }

  const isQuestionAnswered = (questionId) => {
    const question = questionarr[questionId]
    return (
      answerMethod[questionId] === "pdf" ||
      question.user_answer ||
      question.user_image ||
      question.user_displayImage ||
      answerMethod[questionId] === "photo" ||
      (question.mcqOptions && question.mcqOptions.length > 0 && question.user_answer)
    )
  }

  const getAssignmentStats = () => {
    if (!questionarr || questionarr.length === 0) return { total: 0, attempted: 0, notDone: 0 }

    const total = questionarr.length
    const attempted = questionarr.filter((q, index) =>
      assignmentStatus === "saved" ? isQuestionAnswered(index) : q.user_answer || q.user_image || q.user_displayImage,
    ).length
    const notDone = total - attempted

    return { total, attempted, notDone }
  }

  const getCompletionStatus = () => {
    if (!questionarr.length) return 0
    const answeredCount = questionarr.filter((_, index) => isQuestionAnswered(index)).length
    return Math.round((answeredCount / questionarr.length) * 100)
  }

  const getCompletedQuestionsCount = () => {
    return questionarr.filter((_, index) => isQuestionAnswered(index)).length
  }

  const calculateTotalMarks = (questions) => {
    if (!questions || !Array.isArray(questions)) return 0
    return questions.reduce((total, question) => total + (question.maxMarks || 0), 0)
  }

  const calculateObtainedMarks = (questions) => {
    if (!questions || !Array.isArray(questions)) return 0
    return questions.reduce((total, question) => total + (question.givenMarks || 0), 0)
  }

  const navigateToQuestion = (index) => {
    setActiveQuestion(index)
    // Smooth scroll to the question
    const element = document.getElementById(`question-${index}`)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  if (!assignment) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-indigo-800 font-medium">Loading assignment...</p>
        </div>
      </div>
    )
  }

  const stats = getAssignmentStats()
  const obtainedMarks = calculateObtainedMarks(questionarr)
  const maxMarks = calculateTotalMarks(questionarr)

  // Render different views based on assignment status
  const renderSavedAssignmentView = () => {
    const currentQuestion = questionarr[activeQuestion]
    const marks = currentQuestion?.maxMarks || 0

    return (
      <div className="p-3 md:p-4">
        {/* Question Navigation - More compact on mobile */}
        <div className="overflow-x-auto py-3 mb-4">
          <div className="flex gap-2 md:gap-3">
            {questionarr.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveQuestion(index)}
                className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors duration-200 text-xs md:text-sm ${
                  activeQuestion === index
                    ? "bg-indigo-600 text-white shadow-md"
                    : isQuestionAnswered(index)
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                      : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                }`}
              >
                {isQuestionAnswered(index) ? <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5" /> : index + 1}
              </button>
            ))}
            {/* Progress indicator - more compact on mobile */}
            <div className="flex items-center ml-auto">
              <span className="text-xs mr-2">
                {getCompletedQuestionsCount()}/{questionarr.length}
              </span>
              <div className="w-16 md:w-24 rounded-full h-1.5 mr-1 bg-gray-200">
                <div
                  className="h-1.5 rounded-full transition-all duration-500 ease-out bg-blue-500"
                  style={{ width: `${getCompletionStatus()}%` }}
                ></div>
              </div>
              <span className="text-xs">{getCompletionStatus()}%</span>
            </div>
          </div>
        </div>

        {/* Current Question - Better mobile layout */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-4 md:p-6 mb-4 border border-gray-100">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-indigo-100 text-indigo-600 text-sm font-medium">
              {activeQuestion + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-base md:text-lg font-medium text-gray-800">Question</h2>
                <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                  {marks} {marks === 1 ? "Mark" : "Marks"}
                </span>
                <span className="px-2 py-1 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">
                  {currentQuestion?.questionType ||
                    (currentQuestion?.mcqOptions && currentQuestion.mcqOptions.length > 0 ? "MCQ" : "Text")}
                </span>
              </div>
              <div className="text-sm md:text-base text-gray-700 mb-4 leading-relaxed">
                {currentQuestion?.question_text || currentQuestion?.question}
              </div>
            </div>
          </div>

          {currentQuestion?.questionImageUrl && (
            <div className="mb-4 overflow-hidden rounded-lg shadow-sm">
              <img
                src={questionImages[activeQuestion] || currentQuestion.questionImageUrl}
                alt="Question Image"
                className="w-full h-auto object-cover"
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = "/placeholder.svg"
                }}
              />
            </div>
          )}

          <div className="mt-4 space-y-3">
            {currentQuestion?.mcqOptions && currentQuestion.mcqOptions.length > 0 ? (
              <div className="space-y-2">
                {currentQuestion.mcqOptions.map((option, optionIndex) => (
                  <div
                    key={optionIndex}
                    onClick={() => handleMCQAnswer(activeQuestion, option)}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                      currentQuestion.user_answer === option
                        ? "bg-indigo-50 border-indigo-300"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center border ${
                        currentQuestion.user_answer === option ? "border-indigo-500 bg-indigo-500" : "border-gray-400"
                      }`}
                    >
                      {currentQuestion.user_answer === option && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <label className="text-sm flex-1 cursor-pointer">{option}</label>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 min-h-[100px] text-sm"
                    rows="4"
                    value={currentQuestion?.user_answer || ""}
                    onChange={(e) => handleTextAnswer(activeQuestion, e.target.value)}
                    placeholder="Type your answer here..."
                  ></textarea>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center justify-center px-3 py-2 border border-indigo-300 rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 transition-all duration-200 relative cursor-pointer group">
                    <Camera className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-medium">Upload Image</span>
                    <input
                      onChange={(e) => handlePhotoCapture(activeQuestion, e.target.files[0])}
                      className="opacity-0 absolute h-full w-full top-0 left-0 cursor-pointer"
                      type="file"
                      accept="image/*"
                    />
                  </div>
                </div>
              </>
            )}
            <div className="flex">
              {loadingImageIndex === activeQuestion ? (
                <div className="flex justify-center items-center mt-2">
                  <Loader className="animate-spin text-indigo-500 w-6 h-6" />
                  <span className="ml-2 text-indigo-500">Uploading image...</span>
                </div>
              ) : currentQuestion?.user_displayImage ? (
                <div className="mt-4 relative group">
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Edit className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <img
                    src={currentQuestion.user_displayImage || "/placeholder.svg"}
                    alt="Uploaded Answer"
                    className="w-40 h-auto rounded-xl shadow-md border-2 border-indigo-100"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Action Buttons - Better mobile layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
          <button
            onClick={() => activeQuestion > 0 && setActiveQuestion(activeQuestion - 1)}
            disabled={activeQuestion === 0}
            className={`px-3 py-2.5 rounded-lg flex items-center justify-center text-sm ${
              activeQuestion === 0
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
            }`}
          >
            <ArrowLeft className="w-4 h-4 md:mr-1" />
            <span className="hidden md:inline">Previous</span>
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-3 py-2.5 rounded-lg flex items-center justify-center text-sm ${
              saving ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {saving ? (
              <>
                <Loader className="animate-spin w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Saving...</span>
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Save</span>
              </>
            )}
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-3 py-2.5 rounded-lg flex items-center justify-center text-sm ${
              submitting ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            {submitting ? (
              <>
                <Loader className="animate-spin w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Submitting...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Submit</span>
              </>
            )}
          </button>

          <button
            onClick={() => activeQuestion < questionarr.length - 1 && setActiveQuestion(activeQuestion + 1)}
            disabled={activeQuestion === questionarr.length - 1}
            className={`px-3 py-2.5 rounded-lg flex items-center justify-center text-sm ${
              activeQuestion === questionarr.length - 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
            }`}
          >
            <span className="hidden md:inline">Next</span>
            <ArrowLeft className="w-4 h-4 md:ml-1 transform rotate-180" />
          </button>
        </div>
      </div>
    )
  }

  const renderSubmittedOrEvaluatedView = () => {
    return (
      <>
        {/* Question Navigation */}
        <div className="bg-gray-50 p-2 border-b border-gray-200 overflow-x-auto">
          <div className="flex space-x-1 min-w-max">
            {questionarr.map((question, index) => {
              const isEvaluated = assignmentStatus === "evaluated"
              const givenMarks = question.givenMarks !== undefined ? question.givenMarks : null

              return (
                <button
                  key={index}
                  onClick={() => navigateToQuestion(index)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-all ${
                    activeQuestion === index
                      ? "bg-blue-600 text-white shadow-md"
                      : isEvaluated
                        ? givenMarks > 0
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : givenMarks !== null
                            ? "bg-red-100 text-red-800 border border-red-300"
                            : "bg-white text-gray-700 border border-gray-300"
                        : question.user_answer || question.user_image || question.user_displayImage
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {index + 1}
                </button>
              )
            })}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 md:p-6">
          <div className="space-y-6">
            {questionarr.map((question, index) => {
              const marks = question.marks || question.maxMarks || 0
              const questionText = question.question_text || question.question
              const isEvaluated = assignmentStatus === "evaluated"
              const givenMarks = question.givenMarks !== undefined ? question.givenMarks : null

              return (
                <div
                  id={`question-${index}`}
                  key={index}
                  className={`bg-white p-6 rounded-2xl shadow-sm mb-8 transition-all duration-300 border-l-4 ${
                    isEvaluated
                      ? givenMarks > 0
                        ? "border-l-green-500"
                        : givenMarks !== null
                          ? "border-l-red-500"
                          : "border-l-blue-500"
                      : question.user_answer || question.user_image || question.user_displayImage
                        ? "border-l-green-500"
                        : "border-l-blue-500"
                  }`}
                >
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                      <span className="bg-blue-100 text-blue-800 rounded-full w-10 h-10 flex items-center justify-center mr-3 shadow-sm">
                        {index + 1}
                      </span>
                      <span className="hidden sm:inline">Question</span>
                    </h2>
                    <div className="flex items-center">
                      <span className="text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-1.5 rounded-full shadow-sm flex items-center">
                        <Award className="w-4 h-4 mr-1.5" />
                        {marks} {marks === 1 ? "Mark" : "Marks"}
                      </span>
                    </div>
                  </div>

                  <div className="text-lg text-gray-700 mb-6 leading-relaxed">{questionText}</div>

                  {question.questionImageUrl && (
                    <div className="mb-6 flex justify-center">
                      <div className="relative rounded-xl overflow-hidden shadow-md border border-gray-200 max-w-2xl">
                        <img
                          src={questionImages[index] || question.questionImageUrl}
                          alt="Question Image"
                          className="max-w-full h-auto"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = "/placeholder.svg?height=300&width=500"
                          }}
                        />
                        <div className="absolute bottom-0 right-0 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-tl-md">
                          <ImageIcon className="w-3 h-3 inline mr-1" />
                          Question Image
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Answer Display */}
                  <div className="mt-6 space-y-4">
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <h3 className="font-medium text-gray-800 mb-2">Your Answer:</h3>
                      {question.mcqOptions && question.mcqOptions.length > 0 ? (
                        <div className="space-y-3">
                          {question.mcqOptions.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className={`flex items-center p-3 border rounded-xl ${
                                option === question.user_answer && option === question.correct_answer
                                  ? "border-green-500 bg-green-50"
                                  : option === question.user_answer && option !== question.correct_answer
                                    ? "border-red-500 bg-red-50"
                                    : option === question.correct_answer
                                      ? "border-green-300 bg-green-50/30"
                                      : "border-gray-200"
                              }`}
                            >
                              <div
                                className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${
                                  option === question.user_answer
                                    ? option === question.correct_answer
                                      ? "border-green-500 bg-green-500"
                                      : "border-red-500 bg-red-500"
                                    : option === question.correct_answer
                                      ? "border-green-300"
                                      : "border-gray-400"
                                }`}
                              >
                                {option === question.user_answer && <Check className={`w-3 h-3 text-white`} />}
                              </div>
                              <label className="flex-1">
                                <span className={option === question.correct_answer ? "font-medium" : ""}>
                                  {option}
                                </span>
                                {option === question.correct_answer && option !== question.user_answer && (
                                  <span className="ml-2 text-xs text-green-600 font-medium">(Correct answer)</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {question.user_answer || "No answer provided"}
                        </p>
                      )}

                      {question.user_displayImage && (
                        <div className="mt-4">
                          <div className="inline-block relative">
                            <img
                              src={question.user_displayImage || "/placeholder.svg"}
                              alt="Your Answer Image"
                              className="max-w-full h-auto rounded-lg shadow-md border border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null
                                e.target.src = "/placeholder.svg?height=150&width=200"
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {isEvaluated && (
                      <div className={`p-5 rounded-xl shadow-md ${givenMarks > 0 ? "bg-green-50" : "bg-red-50"}`}>
                        <div className="flex flex-wrap items-center justify-between">
                          <div className="flex items-center">
                            <div className={`p-2 rounded-full ${givenMarks > 0 ? "bg-green-100" : "bg-red-100"} mr-3`}>
                              <Pen className={`w-5 h-5 ${givenMarks > 0 ? "text-green-600" : "text-red-600"}`} />
                            </div>
                            <h3 className={`font-semibold ${givenMarks > 0 ? "text-green-800" : "text-red-800"}`}>
                              Evaluation:
                              <span className={`ml-2 font-bold ${givenMarks > 0 ? "text-green-600" : "text-red-600"}`}>
                                {givenMarks !== null ? `${givenMarks}/${marks}` : "Not graded"}
                              </span>
                            </h3>
                          </div>
                        </div>

                        {question.remarks && (
                          <div className="mt-3">
                            <span className="font-medium text-gray-700">Remarks:</span>
                            <p
                              className={`mt-1 p-3 rounded-lg ${givenMarks > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                            >
                              {question.remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer Summary */}
        <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:py-5 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {assignmentStatus === "evaluated" ? (
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-blue-500 mr-2" />
                  <span className="text-gray-700 font-medium">Assignment Evaluated</span>
                </div>
                <div
                  className={`font-bold text-xl ${
                    submissionData?.obtainedMarks > 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  Total Score: {submissionData?.obtainedMarks || obtainedMarks}/{maxMarks}
                </div>
              </div>
            ) : (
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-blue-500 mr-2" />
                  <span className="text-gray-700 font-medium">Assignment Submitted</span>
                </div>
                <div className="text-sm text-gray-500">
                  Submitted on: {new Date(submissionData?.submittedAt || Date.now()).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Main Content Card */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          {/* Assignment Header - Same as Assignment Details */}
          <div
            className="bg-gradient-to-r from-indigo-500 to-blue-600 px-4 md:px-4"
            style={{ paddingTop: "1.375rem", paddingBottom: "1.375rem" }}
          >
            {/* Mobile Layout - REDESIGNED FOR BETTER SPACING */}
            <div className="block lg:hidden">
              {/* Row 1: Back button with more space */}
              <div className="flex items-center mb-4">
                <button
                  onClick={() => navigate("/assignments")}
                  className="flex items-center text-white hover:text-indigo-100 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>

              {/* Row 2: Title with more prominence */}
              <div className="text-center mb-4">
                <h1 className="text-lg font-bold text-white leading-tight">
                  {submissionData?.assignmentTitle ||
                    assignment.assignmentTitle ||
                    assignment.assignment_topic ||
                    "Untitled Assignment"}
                </h1>
              </div>

              {/* Row 3: Stats in a more organized grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="flex flex-col items-center bg-white/20 backdrop-blur-sm rounded-lg px-2 py-2 border border-white/30">
                  <FileText className="w-4 h-4 mb-1 opacity-80" />
                  <span className="text-xs text-white font-medium">Total</span>
                  <span className="text-sm font-bold text-white">{stats.total}</span>
                </div>
                <div className="flex flex-col items-center bg-white/20 backdrop-blur-sm rounded-lg px-2 py-2 border border-white/30">
                  <CheckCircle className="w-4 h-4 mb-1" />
                  <span className="text-xs text-white font-medium">Done</span>
                  <span className="text-sm font-bold text-white">{stats.attempted}</span>
                </div>
                <div className="flex flex-col items-center bg-white/20 backdrop-blur-sm rounded-lg px-2 py-2 border border-white/30">
                  <XCircle className="w-4 h-4 mb-1 opacity-80" />
                  <span className="text-xs text-white font-medium">Left</span>
                  <span className="text-sm font-bold text-white">{stats.notDone}</span>
                </div>
              </div>

              {/* Row 4: Assignment info in two columns */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center justify-center bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/30">
                  <Clock className="w-3 h-3 mr-2" />
                  <span className="text-xs text-white">Due: {dueDate}</span>
                </div>
                <div className="flex items-center justify-center bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/30">
                  <Award className="w-3 h-3 mr-2" />
                  <span className="text-xs font-medium text-white">{totalMarks} Marks</span>
                </div>
              </div>

              {/* Row 5: Status and progress/score */}
              <div className="flex items-center justify-between">
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/30">
                  <FileText className="w-3 h-3 mr-2" />
                  <span className="text-xs text-white capitalize">Status: {assignmentStatus}</span>
                </div>

                {assignmentStatus === "evaluated" && (
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/30">
                    <CheckCircle2 className="w-3 h-3 mr-2" />
                    <span className="text-xs font-medium text-white">
                      Score: {obtainedMarks}/{maxMarks}
                    </span>
                  </div>
                )}

                {assignmentStatus === "saved" && (
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/30">
                    <span className="text-xs text-white mr-2">
                      Q{activeQuestion + 1}/{questionarr.length}
                    </span>
                    <div className="w-12 bg-white/20 rounded-full h-1.5">
                      <div
                        className="bg-white h-1.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${getCompletionStatus()}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:block">
              {/* Back Button */}
              <div className="flex items-center mb-3">
                <button
                  onClick={() => navigate("/assignments")}
                  className="flex items-center text-white hover:text-indigo-100 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>

              {/* Unified Row: Stats, Title, Due Date, Marks, Status */}
              <div className="flex items-center justify-between mb-3">
                {/* Stats (Left) */}
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                    <FileText className="w-3 h-3 opacity-80" />
                    <span>
                      Total: <span className="font-bold">{stats.total}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                    <CheckCircle className="w-3 h-3 opacity-100" />
                    <span>
                      Attempted: <span className="font-bold">{stats.attempted}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                    <XCircle className="w-3 h-3 opacity-80" />
                    <span>
                      Not Done: <span className="font-bold">{stats.notDone}</span>
                    </span>
                  </div>
                </div>

                {/* Title (Center) */}
                <div className="flex-shrink-0">
                  <h1 className="text-base font-bold text-white text-center whitespace-nowrap">
                    {submissionData?.assignmentTitle ||
                      assignment.assignmentTitle ||
                      assignment.assignment_topic ||
                      "Untitled Assignment"}
                  </h1>
                </div>

                {/* Due, Marks, Status & Score (Right) */}
                <div className="flex items-center gap-2 justify-end">
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                    <Clock className="w-3 h-3 mr-1" />
                    <span className="text-xs text-white">Due: {dueDate}</span>
                  </div>
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                    <Award className="w-3 h-3 mr-1" />
                    <span className="text-xs font-medium text-white">{totalMarks} Marks</span>
                  </div>
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                    <FileText className="w-3 h-3 mr-1" />
                    <span className="text-xs text-white capitalize">Status: {assignmentStatus}</span>
                  </div>
                  {assignmentStatus === "evaluated" && (
                    <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      <span className="text-xs font-medium text-white">
                        Score: {obtainedMarks}/{maxMarks}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Render different content based on status */}
          {assignmentStatus === "saved" ? renderSavedAssignmentView() : renderSubmittedOrEvaluatedView()}
        </div>

        {/* Overall Remarks Section for evaluated assignments */}
        {assignmentStatus === "evaluated" && submissionData?.overallRemarks && (
          <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 mb-6">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-4 sm:p-6 text-white">
              <h2 className="text-xl font-bold flex items-center">
                <Pen className="w-5 h-5 mr-2" />
                Overall Feedback
              </h2>
            </div>
            <div className="p-6">
              <div className="prose max-w-none">{renderMarkdown(submissionData.overallRemarks)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Warning Modal for saved assignments */}
      {showWarning && assignmentStatus === "saved" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 md:p-8 rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300 animate-fadeIn">
            <div className="flex items-center text-amber-500 mb-4">
              <AlertCircle className="w-8 h-8 mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Incomplete Submission</h2>
            </div>
            <p className="mb-6 text-gray-700">
              You have not attempted all questions. Are you sure you want to submit your assignment?
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowWarning(false)}
                className="px-5 py-2.5 bg-gray-100 text-gray-800 rounded-xl hover:bg-gray-200 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowWarning(false)
                  submitAssignment()
                }}
                className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 font-medium transition-colors duration-200 shadow-md"
              >
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
